// @ts-strict-ignore
/**
 * An interface for individual report types (Insights, Funnels, etc) to
 * implement for managing their standard building blocks (e.g., hydrating
 * a query builder from a bookmark, rendering a chart from API results, etc.).
 */
import {Location} from 'react-router';

import {JsxFactory} from 'common/util/jsx';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {AdditionalContext, QueryContext, SegmentationMetadata} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {UpsellModalAttrs, UpsellModalProps} from 'common/widgets/upsell-modal';
import {CsvExportSpec, QueryContextFnParams} from 'common/report/report-manager/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {ChartType} from 'common/types/reports/bookmark';

import {QueryReason} from '../util/query-tracking/types';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {ReportTracker, TrackingOptions} from '../report-tracker';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from '../types';
import {ProjectDetails} from '../stores/project-details';
import {CustomEventDefinition} from '../util/saved-entities/types';
import {DashboardBookmarkOverrides, DashboardOrigin} from '../util/bookmark-overrides';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {DisplayBookmark} from '../dashboards/types';
import {IronContextRegistry} from '../context-registry';
import type {QueryManager} from './query';
import {ReportQueryBuilderParams} from './query-builder';
import {ReportVisualizationAttrs, ReportVisualizationProps} from './visualization';
import {ReportHeaderJsxProps} from './report-header';
import {ReportTrackingEvent} from './tracking';

type ReportQueryBuilderJsxProps<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  ErrorType = BaseQueryErrorType,
> = {
  params: ReportQueryBuilderParams<BookmarkParamsType, QueryResultsType, ErrorType>;
};

type ReportVisualizationJsxProps<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType,
> = {
  attrs: ReportVisualizationAttrs;
  props: ReportVisualizationProps<BookmarkParamsType, QueryResultsType, QueryErrorType>;
};

// constructor should be the same for all report manager sublcasses
export type ReportManagerParams = {
  projectId: number;
  workspaceId?: number;
  location: Location;
  insightsReportMode: BookmarkType;
  metricsResource?: MetricsResource;
  behaviorsResource?: BehaviorsResource;
  dashboardOverrides?: DashboardBookmarkOverrides;
};

// add type defaults so that we don't have to wire generics through places where they're not useful.
export abstract class ReportManager<
  BookmarkParamsType = any,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType,
> {
  abstract bookmarkType: BookmarkType;
  abstract reportDisplayName: string;
  abstract tracker: ReportTracker;

  public isCsvExportEnabled = false;
  public declare projectId: number;
  public declare workspaceId: number;
  public declare location: Location;
  public declare insightsReportMode: BookmarkType;
  public declare metricsResource: MetricsResource;
  public declare behaviorsResource: BehaviorsResource;
  public declare dashboardOverrides: DashboardBookmarkOverrides;

  public declare queryBuilderEl: JsxFactory<ReportQueryBuilderJsxProps<BookmarkParamsType>>;
  public declare reportHeaderEl: JsxFactory<ReportHeaderJsxProps>;
  public declare visualizationEl: JsxFactory<
    ReportVisualizationJsxProps<BookmarkParamsType, QueryResultsType, QueryErrorType>
  >;

  public declare queryManager: QueryManager<BookmarkParamsType, QueryResultsType, QueryErrorType>;

  constructor({
    projectId,
    workspaceId,
    location,
    insightsReportMode,
    metricsResource,
    behaviorsResource,
    dashboardOverrides,
  }: ReportManagerParams) {
    this.projectId = projectId;
    this.workspaceId = workspaceId;
    this.location = location;
    this.insightsReportMode = insightsReportMode;
    this.metricsResource = metricsResource;
    this.behaviorsResource = behaviorsResource;
    this.dashboardOverrides = dashboardOverrides;
  }

  getUxSegmentationLimit(_: BookmarkParamsType): number | undefined {
    return undefined;
  }

  getCsvExportLimit(_: {bookmarkParams: BookmarkParamsType; currentProject: CurrentProject}): number | undefined {
    return undefined;
  }

  /**
   * @deprecated use getCsvExportLimit instead.
   * TODO go/j/ae-1194: remove this method when we simplify the logic and move all callers to getCsvExportLimit instead.
   */
  getFixedCsvExportLimit(_: {bookmarkParams: BookmarkParamsType; currentProject: CurrentProject}): number | undefined {
    return undefined;
  }

  async getCsvExportSpec(_params: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
  }): Promise<CsvExportSpec> {
    return Promise.resolve(undefined);
  }

  async csvExportQuery({
    bookmark,
    currentProject,
    useQueryCache = true,
    location,
    limit,
  }: {
    bookmark: DisplayBookmark;
    currentProject: CurrentProject;
    useQueryCache?: boolean;
    location: Location;
    limit: number;
  }) {
    const {result, isSegmentationLimitHit} = await this.queryManager.csvExportQuery({
      reportQueryOrigin: this.insightsReportMode,
      bookmarkParams: bookmark.params,
      currentProject,
      useQueryCache,
      location,
      limit,
    });
    const title = bookmark.name || `Untitled Report`;
    const filename = `${title}.csv`;
    return {
      result,
      filename,
      isSegmentationLimitHit,
    };
  }

  query({
    bookmarkParams,
    currentProject,
    queryReason,
    bookmarkId,
    hasUnsavedBookmarkChanges,
    useQuerySampling,
    useQueryCache = true,
    location,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
    queryReason: QueryReason;
    bookmarkId: null | number;
    hasUnsavedBookmarkChanges: boolean;
    useQuerySampling: boolean;
    useQueryCache?: boolean;
    location: Location;
  }): Promise<QueryResultsType | QueryErrorType> {
    return this.queryManager.query({
      reportQueryOrigin: this.insightsReportMode,
      bookmarkParams,
      currentProject,
      queryReason,
      bookmarkId,
      hasUnsavedBookmarkChanges,
      useQuerySampling,
      useQueryCache,
      location,
    });
  }

  abortPendingQuery() {
    this.queryManager.abortPendingQuery();
  }

  handleResults(
    paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>,
  ): ParamsAndResults<BookmarkParamsType, QueryResultsType> {
    return this.queryManager.handleResults(paramsAndResults);
  }

  /**
   * Optional method to convert segmentation data from a query response into a standardized format
   */
  getSegmentationMetadatas(_result: QueryResultsType): SegmentationMetadata[] | null | undefined {
    return null;
  }

  /**
   * Optional method to overwrite describing what kind of analysis the chart is doing.
   * e.g. "Time to Convert", "Logarithmic"
   */
  getAnalysisDescription(_params: BookmarkParamsType): string {
    return null;
  }

  track(event: string, props: Obj = {}, trackingOptions?: TrackingOptions) {
    this.tracker.track(event, props, trackingOptions ?? {});
  }

  /**
   * Optional method to override if the report supports data tables.
   * Use bookmark params to determine if the viz type supports data tables.
   */
  hasDataTable(_params: BookmarkParamsType): boolean {
    return false;
  }

  /**
   * Optional method to render an upsell if a report is restricted. This is just for flows, and only applies to some legacy plans.
   * TODO: Remove this when flows is available for all plans.
   */
  public getReportUpsell(_project: CurrentProject): {attrs: UpsellModalAttrs; props: UpsellModalProps} | null {
    return null;
  }

  public trackQuery({
    eventName,
    params,
    project,
    extraProps,
    error,
    availableContexts,
  }: {
    eventName: ReportTrackingEvent;
    params: BookmarkParamsType;
    project: CurrentProject;
    extraProps: Record<string, any>;
    error?: QueryErrorType;
    availableContexts: Pick<IronContextRegistry, `metrics` | `behaviors` | `i18n`>;
  }) {
    this.tracker.track(eventName, {
      ...this.getClientQueryTrackingProps({params, project, error, availableContexts}),
      ...extraProps,
    });
  }

  abstract getClientQueryTrackingProps(params: {
    params: BookmarkParamsType;
    project: CurrentProject;
    error?: QueryErrorType;
    availableContexts: Pick<IronContextRegistry, `metrics` | `behaviors` | `i18n`>;
  }): ClientQueryTrackingProps;

  abstract getBookmarkForSaving(params: BookmarkParamsType): BookmarkParamsType;

  abstract hasUnsavedBookmarkParams(original: BookmarkParamsType, edited: BookmarkParamsType): boolean;

  abstract getDefaultBookmarkParams(project: CurrentProject | ProjectDetails): BookmarkParamsType;

  /**
   * Determine whether results have data to display
   */
  abstract isEmptyResults(results: QueryResultsType, params?: BookmarkParamsType): boolean;

  /**
   * Determine whether bookmark params are fully formed enough to make a query
   */
  abstract isQueryable(params: BookmarkParamsType): boolean;

  /**
   * All reports have query types where a date range is specified. If a query type doesn't have a date range, return null.
   */
  abstract getDateRange(params: BookmarkParamsType): DateRange | null;

  abstract getBookmarkParamsFromQueryContext(context: QueryContext, params: QueryContextFnParams): BookmarkParamsType;

  abstract getQueryContextFromBookmarkParams({
    additionalContext,
    behaviorsResource,
    bookmarkType,
    customEvents,
    dataGroups,
    originDashboard,
    metricsResource,
    params,
    querySamplingEnabled,
    project,
  }: {
    additionalContext: AdditionalContext;
    behaviorsResource: BehaviorsResource;
    bookmarkType: BookmarkType;
    customEvents: CustomEventDefinition[];
    dataGroups: DataGroup[];
    originDashboard: DashboardOrigin;
    metricsResource: MetricsResource;
    params: BookmarkParamsType;
    project: CurrentProject | ProjectDetails;
    querySamplingEnabled: boolean;
  }): QueryContext;

  /**
   * Unfortunately, different report API's respond with different keys for query sampling factor.
   */
  abstract getQuerySamplingFactor(results: QueryResultsType): number;

  /** called when a bookmark is about to be saved, a report manager can abort saving based on report state */
  public handleBookmarkSave(
    bookmarkParams: BookmarkParamsType,
    _availableContexts: Partial<IronContextRegistry>,
  ): null | BookmarkParamsType | Promise<BookmarkParamsType | null> {
    return bookmarkParams;
  }

  public getCommonChartType(_params: BookmarkParamsType): ChartType {
    return null;
  }

  /** allows certain chart types to route to alternate report managers */
  public reportSwitchInfoForChartType(_report: ChartType): {
    report: BookmarkType;
    additionalContext: AdditionalContext;
  } {
    return null;
  }

  /**
   * Ensures necessary api resources are loaded.
   *
   * Currently used to await
   * 1. MetricsResource if there is a saved formula
   * - we need to hydrate saved formulas' referencedMetrics from the formula's id before we can apply necessary report restrictions and query param modifications
   * 2. BehaviorsResource if there is a saved behavior
   * - need to hydrate saved behavior definitions
   */
  public async waitForApiResources({
    bookmarkParams: _bookmarkParams,
  }: {
    bookmarkParams: BookmarkParamsType;
  }): Promise<void> {
    return Promise.resolve();
  }
}
